import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, API } from "@/lib/api";
import { FAMILIES } from "@/lib/format";
import UnitCard from "@/components/UnitCard";
import { Search, SlidersHorizontal, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CHIPS = ["M1", "M1 Pro", "M1 Max", "M2", "M2 Pro", "M3", "M3 Pro", "M4", "A18 Pro", "Intel Core i5", "Intel Core i7", "Intel Core i9", "Intel Core M"];

export default function Inventory() {
  const [params] = useSearchParams();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(params.get("status") || "All");
  const [family, setFamily] = useState("All");
  const [chip, setChip] = useState("All");
  const [sort, setSort] = useState("newest");
  const [showFilters, setShowFilters] = useState(!!params.get("status"));
  const [pdfLoading, setPdfLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/units", {
        params: { search: search || undefined, status, family, chip, sort },
      });
      setUnits(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, status, family, chip, sort]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get("/in-stock-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPdfLoading(false);
    }
  };

  const selectCls = "h-10 rounded-xl border border-white/10 bg-black px-3 text-sm text-white outline-none focus:border-[#2997ff]";

  return (
    <div className="mf-fade-in">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Inventory</h1>
          <p className="text-sm text-[#98989d]">{units.length} units</p>
        </div>
        <button
          data-testid="export-in-stock-pdf-button"
          onClick={openPdf}
          disabled={pdfLoading}
          className="flex items-center gap-2 rounded-full border border-white/10 bg-[#161618] px-4 py-2 text-sm font-semibold transition-colors active:scale-95"
        >
          {pdfLoading ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}
          In-Stock PDF
        </button>
      </div>

      <div className="mb-3 flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98989d]" />
          <input
            data-testid="search-inventory-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code, model, chip, serial…"
            className="h-11 w-full rounded-xl border border-white/10 bg-[#121214] pl-9 pr-3 text-sm outline-none focus:border-[#2997ff]"
          />
        </div>
        <button
          data-testid="toggle-filters-button"
          onClick={() => setShowFilters((s) => !s)}
          className={`flex h-11 w-11 items-center justify-center rounded-xl border transition-colors ${showFilters ? "border-[#2997ff] text-[#2997ff]" : "border-white/10 text-[#98989d]"}`}
        >
          <SlidersHorizontal size={18} />
        </button>
      </div>

      {showFilters && (
        <div className="mf-fade-in mb-4 grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-[#121214] p-3 sm:grid-cols-4">
          <select data-testid="filter-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
            {["All", "In stock", "Reserved", "Sold", "Hidden"].map((s) => <option key={s}>{s}</option>)}
          </select>
          <select data-testid="filter-family-select" value={family} onChange={(e) => setFamily(e.target.value)} className={selectCls}>
            {["All", ...FAMILIES].map((s) => <option key={s}>{s}</option>)}
          </select>
          <select data-testid="filter-chip-select" value={chip} onChange={(e) => setChip(e.target.value)} className={selectCls}>
            {["All", ...CHIPS].map((s) => <option key={s}>{s}</option>)}
          </select>
          <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls}>
            <option value="newest">Newest</option>
            <option value="price_low">Price: low to high</option>
            <option value="price_high">Price: high to low</option>
          </select>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#2997ff]" /></div>
      ) : units.length === 0 ? (
        <div className="py-20 text-center text-[#98989d]">No units match your search.</div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {units.map((u) => <UnitCard key={u.id} unit={u} onUpdated={(upd) => setUnits((list) => list.map((x) => (x.id === upd.id ? upd : x)))} />)}
        </div>
      )}
    </div>
  );
}
